import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, GraduationCap, Star, Copy, PartyPopper, Share2, X, Award } from "lucide-react";
import { toast } from "sonner";
import { confirmGrade } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

/**
 * ConfirmGradePanel — student-facing "Confirm my official grade" flow.
 * 3 states:
 *   1. CTA (has not yet confirmed)
 *   2. Modal form (open)
 *   3. Success (celebration + share prompt)
 *
 * Guests are prompted to sign in first — only authenticated users can submit,
 * so records entering calibration_records are trustworthy.
 */
export default function ConfirmGradePanel({ analysisId, myaePrediction, alreadyConfirmed = false, existingGrade = null }) {
  const { user } = useAuth();
  const [openModal, setOpenModal] = useState(false);
  const [success, setSuccess] = useState(alreadyConfirmed ? { existing: true, official: existingGrade } : null);

  // form state
  const [official, setOfficial] = useState("");
  const [classification, setClassification] = useState("");
  const [markerFeedback, setMarkerFeedback] = useState("");
  const [module, setModule] = useState("");
  const [rating, setRating] = useState(0);
  const [improvement, setImprovement] = useState("");
  const [isFinal, setIsFinal] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseFloat(official);
    if (isNaN(n) || n < 0 || n > 100) { toast.error("Enter a percentage between 0 and 100."); return; }
    if (!isFinal) { toast.error("Please tick the box confirming this is your final grade."); return; }
    setSaving(true);
    try {
      const res = await confirmGrade(analysisId, {
        official_percentage: n,
        classification, marker_feedback: markerFeedback,
        module, accuracy_rating: rating || null,
        improvement_feedback: improvement,
        is_final: true,
      });
      setSuccess(res);
      setOpenModal(false);
      toast.success(`Thanks! +${res.points_awarded} community points.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your grade.");
    } finally { setSaving(false); }
  };

  // --- Guest gate ---
  if (!user) {
    return (
      <div className="no-print mt-8 rounded-2xl border border-zinc-200 p-5" data-testid="confirm-grade-signin">
        <p className="overline text-indigo-500 mb-1">Received your university mark?</p>
        <p className="text-sm text-zinc-700 mb-3">Sign in to confirm your official grade — it helps improve MyAE&#39;s accuracy for future students and earns you community points.</p>
        <Link to="/login" data-testid="confirm-grade-signin-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 transition-colors">
          <GraduationCap size={16} /> Sign in to confirm my grade
        </Link>
      </div>
    );
  }

  // --- Success / celebration state ---
  if (success && !success.existing) {
    const diff = success.difference;
    return (
      <div className="no-print mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="confirm-grade-success">
        <div className="flex items-start gap-3 mb-4">
          <PartyPopper className="text-emerald-600 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-serif text-lg text-zinc-900">Thanks for helping improve MyAE.</p>
            <p className="text-sm text-zinc-600">Your confirmation earned <b>{success.points_awarded} community points</b> and <b>{success.entries_this_month} prize-draw entries</b> this month.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 py-3 border-y border-emerald-100 text-center font-mono text-sm">
          <div><p className="text-[10px] uppercase tracking-wider text-zinc-500">MyAE predicted</p><p className="text-lg text-zinc-900">{success.myae_prediction ?? myaePrediction}%</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-zinc-500">Official grade</p><p className="text-lg text-emerald-700">{success.official_percentage}%</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-zinc-500">Difference</p><p className={`text-lg ${Math.abs(diff) <= 5 ? "text-emerald-600" : Math.abs(diff) <= 10 ? "text-amber-600" : "text-rose-600"}`}>{diff > 0 ? "+" : ""}{diff}pp</p></div>
        </div>
        {/* Share prompt */}
        <div className="mt-5 pt-4 border-t border-emerald-100">
          <p className="text-sm text-zinc-700 mb-2 flex items-center gap-2"><Share2 size={14} className="text-emerald-600" /> Invite a friend — you both get bonus credits.</p>
          <div className="flex items-center gap-2">
            <input readOnly value={success.referral_link} data-testid="confirm-grade-referral-link"
              className="flex-1 min-w-0 font-mono text-xs rounded-lg border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            <button onClick={() => { navigator.clipboard?.writeText(success.referral_link); toast.success("Referral link copied."); }}
              data-testid="confirm-grade-copy-referral"
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:border-indigo-500 hover:text-indigo-600">
              <Copy size={12} /> Copy
            </button>
          </div>
          <Link to="/community" data-testid="confirm-grade-community-link" className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
            <Award size={13} /> View my community profile
          </Link>
        </div>
      </div>
    );
  }

  // --- Already-confirmed (page reload) ---
  if (success && success.existing) {
    return (
      <div className="no-print mt-8 rounded-2xl border border-zinc-200 p-5" data-testid="confirm-grade-existing">
        <p className="overline text-emerald-600 mb-1">Grade confirmed</p>
        <p className="text-sm text-zinc-700">You confirmed your official grade as <b>{existingGrade}%</b>. Thanks for contributing to MyAE&#39;s calibration.</p>
      </div>
    );
  }

  // --- Default CTA ---
  return (
    <>
      <div className="no-print mt-8 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-6" data-testid="confirm-grade-cta">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[240px]">
            <p className="overline text-indigo-500 mb-1 flex items-center gap-1.5"><GraduationCap size={14} /> Received your official grade?</p>
            <p className="text-sm text-zinc-700">Confirm your university mark to help improve MyAE&#39;s accuracy — and earn community points + a monthly prize-draw entry.</p>
            <p className="text-xs text-zinc-500 mt-1">Your grade is fully anonymised before it contributes to calibration.</p>
          </div>
          <button onClick={() => setOpenModal(true)} data-testid="confirm-grade-open-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 transition-colors">
            <GraduationCap size={16} /> Confirm my official grade
          </button>
        </div>
      </div>

      {/* --- Modal --- */}
      <AnimatePresence>
        {openModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !saving && setOpenModal(false)} data-testid="confirm-grade-modal">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-lg my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
                <p className="font-serif text-lg">Confirm my official grade</p>
                <button onClick={() => setOpenModal(false)} disabled={saving} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Official percentage <span className="text-rose-600">*</span></label>
                  <input type="number" min="0" max="100" step="0.5" value={official} onChange={(e) => setOfficial(e.target.value)}
                    placeholder="e.g. 68" data-testid="confirm-grade-percentage"
                    className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Classification</label>
                    <input value={classification} onChange={(e) => setClassification(e.target.value)}
                      placeholder="e.g. 2:1, First, Merit" data-testid="confirm-grade-classification"
                      className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Module (optional)</label>
                    <input value={module} onChange={(e) => setModule(e.target.value)}
                      placeholder="e.g. MOD402" data-testid="confirm-grade-module"
                      className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Marker feedback (optional)</label>
                  <textarea rows={2} value={markerFeedback} onChange={(e) => setMarkerFeedback(e.target.value)}
                    placeholder="Paste any comments from your marker" data-testid="confirm-grade-marker-feedback"
                    className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
                <div className="border-t border-zinc-100 pt-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">How accurate was MyAE?</label>
                  <div className="flex items-center gap-1 mb-3" data-testid="confirm-grade-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setRating(n)}
                        data-testid={`confirm-grade-star-${n}`}
                        className={`p-1 transition-colors ${rating >= n ? "text-amber-500" : "text-zinc-300 hover:text-zinc-400"}`}>
                        <Star size={22} fill={rating >= n ? "currentColor" : "none"} />
                      </button>
                    ))}
                    <span className="ml-2 text-xs text-zinc-500">{
                      rating === 5 ? "Extremely accurate" : rating === 4 ? "Accurate" :
                      rating === 3 ? "Mostly accurate" : rating === 2 ? "Needs improvement" :
                      rating === 1 ? "Poor prediction" : "Optional"}</span>
                  </div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">What could MyAE have done better?</label>
                  <textarea rows={2} value={improvement} onChange={(e) => setImprovement(e.target.value)}
                    placeholder="Optional — helps us improve" data-testid="confirm-grade-improvement"
                    className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
                <label className="flex items-start gap-2 text-sm text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={isFinal} onChange={(e) => setIsFinal(e.target.checked)}
                    data-testid="confirm-grade-is-final"
                    className="mt-1 rounded border-zinc-300 focus:ring-indigo-500" />
                  <span>I confirm this is my final university grade for this assignment.</span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200">
                <button onClick={() => setOpenModal(false)} disabled={saving}
                  className="text-sm text-zinc-600 hover:text-zinc-900 px-3 py-2">Cancel</button>
                <button onClick={submit} disabled={saving || !isFinal || !official}
                  data-testid="confirm-grade-submit-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <GraduationCap size={14} />} Submit &amp; earn points
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
